// Dark-first related content block.
import React from "react";
import { Link } from "react-router-dom";
import { Overline } from "@/components/Primitives";
import { SERVICES } from "@/data/services";
import { INDUSTRIES } from "@/data/industries";
import { NEEDS } from "@/data/needs";
import { BLOG_POSTS } from "@/data/blog";
import { EPISODES } from "@/data/podcasts";
import { PORTFOLIO } from "@/data/portfolio";

const Section = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <Overline color="yellow" className="mb-3">{title}</Overline>
    <ul className="space-y-2">{children}</ul>
  </div>
);

const RelatedContent = ({
  serviceSlugs = [],
  needSlugs = [],
  industrySlugs = [],
  blogSlugs = [],
  episodeSlugs = [],
  portfolioSlugs = [],
  heading = "Related paths",
  intro,
}) => {
  const services = SERVICES.filter((s) => serviceSlugs.includes(s.slug));
  const needs = NEEDS.filter((n) => needSlugs.includes(n.slug));
  const industries = INDUSTRIES.filter((i) => industrySlugs.includes(i.slug));
  const posts = BLOG_POSTS.filter((p) => blogSlugs.includes(p.slug));
  const episodes = EPISODES.filter((e) => episodeSlugs.includes(e.slug));
  const portfolio = PORTFOLIO.filter((p) => portfolioSlugs.includes(p.slug));

  if (!services.length && !needs.length && !industries.length && !posts.length && !episodes.length && !portfolio.length) {
    return null;
  }

  return (
    <section data-testid="related-content" className="py-20 lg:py-28 bg-black border-t border-white/10">
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline color="yellow" className="mb-3">{heading}</Overline>
            <h2 className="text-3xl lg:text-5xl font-black tracking-tighter leading-[0.95] text-white">
              Keep moving with a useful next step.
            </h2>
            {intro ? <p className="mt-4 text-white/70">{intro}</p> : null}
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.length ? (
              <Section title="Related services" testId="related-services">
                {services.map((s) => (
                  <li key={s.slug}>
                    <Link
                      data-testid={`related-service-${s.slug}`}
                      to={`/services/${s.slug}`}
                      className="k-link text-white/90 font-medium hover:text-[#FFC600]"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
            {needs.length ? (
              <Section title="Related needs" testId="related-needs">
                {needs.map((n) => (
                  <li key={n.slug}>
                    <Link to={`/needs/${n.slug}`} className="k-link text-white/90 font-medium hover:text-[#FFC600]">
                      {n.name}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
            {industries.length ? (
              <Section title="Related industries" testId="related-industries">
                {industries.map((i) => (
                  <li key={i.slug}>
                    <Link to={`/industries/${i.slug}`} className="k-link text-white/90 font-medium hover:text-[#FFC600]">
                      {i.name}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
            {posts.length ? (
              <Section title="Related notes" testId="related-blog">
                {posts.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`} className="k-link text-white/90 font-medium hover:text-[#FFC600]">
                      {p.title}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
            {episodes.length ? (
              <Section title="Related radio" testId="related-radio">
                {episodes.map((e) => (
                  <li key={e.slug}>
                    <Link to={`/radio/${e.slug}`} className="k-link text-white/90 font-medium hover:text-[#FFC600]">
                      Ep {e.number}. {e.title}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
            {portfolio.length ? (
              <Section title="Related work" testId="related-portfolio">
                {portfolio.map((p) => (
                  <li key={p.slug}>
                    <Link to={`/portfolio/${p.slug}`} className="k-link text-white/90 font-medium hover:text-[#FFC600]">
                      {p.title}
                    </Link>
                  </li>
                ))}
              </Section>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RelatedContent;
